/**
 * Widget Theme Configuration
 * Defines customizable appearance settings for the chat widget
 */

export type WidgetTheme = {
  // Header customization
  headerText: string;
  headerBackgroundColor: string;
  headerTextColor: string;

  // Agent name (displayed in message bubbles)
  agentName: string;

  // Logo/Icon image URL
  iconUrl?: string;

  // Accent colors
  accentColor: string;
  textOverAccentColor: string;

  // Button customization
  buttonColor: string;
  buttonText?: string;
  buttonTextColor: string;

  // Footer/Disclaimer
  disclaimerText: string;

  // Dark mode colors
  darkHeaderBackgroundColor: string;
  darkHeaderTextColor: string;
  darkAccentColor: string;
  darkTextOverAccentColor: string;
  darkButtonColor: string;
  darkButtonTextColor: string;

  // Input placeholder
  messagePlaceholder: string;

  // Options display
  maxVisibleOptions: number;
};

/**
 * Default dark mode theme values
 */
export const DEFAULT_DARK_THEME = {
  darkHeaderBackgroundColor: '#1f2937',
  darkHeaderTextColor: '#FFFFFF',
  darkAccentColor: '#374151',
  darkTextOverAccentColor: '#FFFFFF',
  darkButtonColor: '#374151',
  darkButtonTextColor: '#FFFFFF'
};

/**
 * Static UI colors for light and dark mode (matches Tailwind gray palette)
 * Used for non-customizable elements like chat background, bot messages, etc.
 */
export const WIDGET_UI_COLORS = {
  light: {
    chatBg: '#ffffff', // white
    botMessageBg: '#f3f4f6', // gray-100
    botMessageText: '#374151', // gray-700
    inputBg: '#ffffff', // white
    inputBorder: undefined,
    placeholderText: '#6b7280', // gray-500
    poweredByText: undefined,
    headerTitleText: '#111827' // gray-900
  },
  dark: {
    chatBg: '#030712', // gray-950
    botMessageBg: '#1f2937', // gray-800
    botMessageText: '#e5e7eb', // gray-200
    inputBg: '#111827', // gray-900
    inputBorder: '#374151', // gray-700
    placeholderText: '#9ca3af', // gray-400
    poweredByText: '#9ca3af', // gray-400
    headerTitleText: '#f9fafb' // gray-50
  }
} as const;

/**
 * Default theme values matching current hardcoded design
 */
export const DEFAULT_THEME: WidgetTheme = {
  headerText: 'AI Support',
  agentName: 'AI Assistant',
  headerBackgroundColor: '#000000',
  headerTextColor: '#FFFFFF',
  iconUrl: '',
  accentColor: '#030712',
  textOverAccentColor: '#FFFFFF',
  buttonColor: '#000000',
  buttonText: 'Chat',
  buttonTextColor: '#FFFFFF',
  disclaimerText: '',
  darkHeaderBackgroundColor: DEFAULT_DARK_THEME.darkHeaderBackgroundColor,
  darkHeaderTextColor: DEFAULT_DARK_THEME.darkHeaderTextColor,
  darkAccentColor: DEFAULT_DARK_THEME.darkAccentColor,
  darkTextOverAccentColor: DEFAULT_DARK_THEME.darkTextOverAccentColor,
  darkButtonColor: DEFAULT_DARK_THEME.darkButtonColor,
  darkButtonTextColor: DEFAULT_DARK_THEME.darkButtonTextColor,
  messagePlaceholder: 'Message...',
  maxVisibleOptions: 5
};

/**
 * Validates and merges partial theme with defaults
 */
export const mergeTheme = (partial?: Partial<WidgetTheme>): WidgetTheme => {
  return {
    ...DEFAULT_THEME,
    ...partial
  };
};

/**
 * Resolves theme colors for dark mode
 * Returns the theme with dark mode colors applied to the primary color fields
 */
export const resolveThemeForDarkMode = (theme: WidgetTheme, darkMode: boolean): WidgetTheme => {
  if (!darkMode) return theme;
  return {
    ...theme,
    headerBackgroundColor: theme.darkHeaderBackgroundColor,
    headerTextColor: theme.darkHeaderTextColor,
    accentColor: theme.darkAccentColor,
    textOverAccentColor: theme.darkTextOverAccentColor,
    buttonColor: theme.darkButtonColor,
    buttonTextColor: theme.darkButtonTextColor
  };
};
